/**
 * 中继连接管理 —— 多中继候选 + 指数退避重连 + 连接状态机（ARCH §3.6）。
 *
 * 本文件**不依赖 libp2p**：拨号动作由外部注入（`connect`），因此可纯逻辑单测。
 *
 * 状态机：`disconnected → connecting → connected → ready`
 *  - `connected`：底层拨号成功（libp2p 已建立连接）
 *  - `ready`：完成首轮目录上报 + 邮箱拉取，业务可用
 *  - 任一环节失败 → 换下一个候选地址，候选耗尽后按指数退避整体重试
 */

import { MeshLinkError } from '@/lib/result';
import { createLogger } from '@/lib/logger';

const logger = createLogger('net:connection');

/** 中继连接状态机。 */
export type RelayConnState = 'disconnected' | 'connecting' | 'connected' | 'ready';

/** UI / 传输统一的中继状态类型（= `RelayConnState`，便于 store 与 net 共用一套字面量）。 */
export type RelayStatus = RelayConnState;

/** 中继候选地址（带可选 PeerID，便于跳过发现）。 */
export interface RelayEndpoint {
  addr: string;
  peerId?: string;
}

export interface RelayConnectionOptions {
  /** 候选地址列表（按优先级）。 */
  endpoints: RelayEndpoint[];
  /** 拨号实现（成功 resolve，失败 reject）。 */
  connect(endpoint: RelayEndpoint): Promise<void>;
  /** 状态变化回调（驱动 UI 状态灯）。 */
  onState?(state: RelayConnState, info: { addr?: string; attempt: number; error?: string }): void;
  /** 退避参数。 */
  baseDelayMs?: number;
  maxDelayMs?: number;
  factor?: number;
  /** 单个候选的重试次数。 */
  attemptsPerEndpoint?: number;
  /** 抖动比例（0~0.5），避免多端同时重连造成惊群。 */
  jitterRatio?: number;
  /**
   * 重启时等待旧 loop 退出的上界（ms）；旧 loop 可能卡在不可取消的 `connect()` 内，故有界等待。
   * 默认 250。
   */
  restartGraceMs?: number;
  /** 注入的 sleep（单测用）。 */
  sleep?: (ms: number) => Promise<void>;
}

export const DEFAULT_BACKOFF = {
  baseDelayMs: 1_000,
  maxDelayMs: 30_000,
  factor: 2,
  attemptsPerEndpoint: 3,
  jitterRatio: 0.2,
} as const;

/**
 * 指数退避延时（含抖动）。
 * 第 n 次重试（n 从 1 开始）：`base * factor^(n-1)`，上限 `maxDelayMs`。
 */
export function backoffDelay(attempt: number, opts: Pick<RelayConnectionOptions, 'baseDelayMs' | 'maxDelayMs' | 'factor' | 'jitterRatio'> = {}): number {
  const base = opts.baseDelayMs ?? DEFAULT_BACKOFF.baseDelayMs;
  const max = opts.maxDelayMs ?? DEFAULT_BACKOFF.maxDelayMs;
  const factor = opts.factor ?? DEFAULT_BACKOFF.factor;
  const jitterRatio = opts.jitterRatio ?? DEFAULT_BACKOFF.jitterRatio;

  const exp = Math.min(max, base * Math.pow(factor, Math.max(0, attempt - 1)));
  const jitter = exp * jitterRatio * Math.random();
  return Math.round(exp + jitter);
}

/** 多中继 + 指数退避的连接管理器。 */
export class RelayConnectionManager {
  private _state: RelayConnState = 'disconnected';
  private _attempt = 0;
  private _activeAddr: string | null = null;
  private _running = false;
  private _loopPromise: Promise<void> | null = null;
  private _abort = false;
  /** loop 代际令牌：`restart()`/`start()` 自增即令旧 loop 失效（无需依赖 await）。 */
  private _loopToken = 0;

  private readonly baseDelayMs: number;
  private readonly maxDelayMs: number;
  private readonly factor: number;
  private readonly attemptsPerEndpoint: number;
  private readonly jitterRatio: number;
  private readonly restartGraceMs: number;
  private readonly sleep: (ms: number) => Promise<void>;

  constructor(private readonly opts: RelayConnectionOptions) {
    this.baseDelayMs = opts.baseDelayMs ?? DEFAULT_BACKOFF.baseDelayMs;
    this.maxDelayMs = opts.maxDelayMs ?? DEFAULT_BACKOFF.maxDelayMs;
    this.factor = opts.factor ?? DEFAULT_BACKOFF.factor;
    this.attemptsPerEndpoint = opts.attemptsPerEndpoint ?? DEFAULT_BACKOFF.attemptsPerEndpoint;
    this.jitterRatio = opts.jitterRatio ?? DEFAULT_BACKOFF.jitterRatio;
    this.restartGraceMs = opts.restartGraceMs ?? 250;
    this.sleep = opts.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
  }

  get state(): RelayConnState {
    return this._state;
  }

  get attempt(): number {
    return this._attempt;
  }

  get activeAddr(): string | null {
    return this._activeAddr;
  }

  /** 候选列表（可运行时替换：例如从 /relay-info 发现后）。 */
  get endpoints(): RelayEndpoint[] {
    return this.opts.endpoints;
  }

  setEndpoints(endpoints: RelayEndpoint[]): void {
    this.opts.endpoints.splice(0, this.opts.endpoints.length, ...endpoints);
  }

  /** 追加候选；`prioritize=true` 时插到最前。返回是否为新增（已存在则 `false`，不重复插入）。 */
  addEndpoint(ep: RelayEndpoint, opts: { prioritize?: boolean } = {}): boolean {
    if (this.opts.endpoints.some((e) => e.addr === ep.addr)) return false;
    if (opts.prioritize) this.opts.endpoints.unshift(ep);
    else this.opts.endpoints.push(ep);
    return true;
  }

  /** 移除候选（按 `addr` 精确匹配）。返回是否命中。 */
  removeEndpoint(addr: string): boolean {
    const idx = this.opts.endpoints.findIndex((e) => e.addr === addr);
    if (idx < 0) return false;
    this.opts.endpoints.splice(idx, 1);
    return true;
  }

  /** 当前候选快照（供 UI 展示 / 去重）。 */
  listEndpoints(): readonly RelayEndpoint[] {
    return [...this.opts.endpoints];
  }

  private setState(state: RelayConnState, info: { addr?: string; error?: string } = {}): void {
    if (this._state === state && !info.error) {
      // 状态未变且不携带错误时不重复回调，避免 UI 抖动
      if (state !== 'connecting') return;
    }
    this._state = state;
    this.opts.onState?.(state, { addr: info.addr ?? this._activeAddr ?? undefined, attempt: this._attempt, error: info.error });
  }

  /**
   * 启动连接循环：逐个候选尝试（每候选 `attemptsPerEndpoint` 次），
   * 全部失败后按指数退避整体重试，直到 `stop()`。
   */
  async start(): Promise<void> {
    if (this._running) return this._loopPromise ?? Promise.resolve();
    this._running = true;
    this._abort = false;
    const token = ++this._loopToken;
    this._loopPromise = this.loop(token);
    return this._loopPromise;
  }

  private async loop(token: number): Promise<void> {
    while (!this._abort && token === this._loopToken) {
      for (const endpoint of this.opts.endpoints) {
        for (let n = 1; n <= this.attemptsPerEndpoint; n += 1) {
          // ① 内层 for 开头：陈旧 loop 静默退出（不写状态）
          if (this._abort || token !== this._loopToken) return;
          this._attempt += 1;
          this.setState('connecting', { addr: endpoint.addr });
          try {
            await this.opts.connect(endpoint);
            // ② connect 成功之后、写状态之前：陈旧 loop 即使拨号成功也不得写状态
            if (this._abort || token !== this._loopToken) return;
            this._activeAddr = endpoint.addr;
            this._attempt = 0;
            this.setState('connected', { addr: endpoint.addr });
            return;
          } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            logger.warn('中继连接失败', { addr: endpoint.addr, attempt: n, err: message });
            // ③ setState('disconnected') 之前：陈旧 loop 不得把健康状态翻回断开
            if (this._abort || token !== this._loopToken) return;
            this.setState('disconnected', { addr: endpoint.addr, error: message });
            const delay = backoffDelay(n, {
              baseDelayMs: this.baseDelayMs,
              maxDelayMs: this.maxDelayMs,
              factor: this.factor,
              jitterRatio: this.jitterRatio,
            });
            await this.sleep(delay);
          }
        }
      }

      // ④ 整体退避之前
      if (this._abort || token !== this._loopToken) return;
      // 一轮全部候选失败 → 整体退避后重来
      const delay = backoffDelay(this.attemptsPerEndpoint + 1, {
        baseDelayMs: this.baseDelayMs,
        maxDelayMs: this.maxDelayMs,
        factor: this.factor,
        jitterRatio: this.jitterRatio,
      });
      logger.warn('所有中继候选不可用，退避后重试', { delayMs: delay });
      await this.sleep(delay);
    }
  }

  /** 标记为业务可用（`ready`）—— 由 `HybridTransport` 在完成首轮同步后调用。 */
  markReady(): void {
    if (this._state === 'connected' || this._state === 'ready') this.setState('ready');
  }

  /**
   * 停止当前 loop 并等待其**真正退出**后重启（根除并发 loop，见 §6.1）。
   *
   * 语义：返回的 Promise 在「旧 loop 已终止」后 resolve（**不是**等新 loop 结束）——
   * 因此新 loop 用 `void this.start()` 启动而非 `await`：`start()` 返回的是 **loop 自身的 Promise**
   * （仅在 loop 因连上/中止而 return 时才 settle），若 await 它，`await reconnect()` 会被永不 settle
   * 的新 loop 挂住（这正是旧实现的隐性挂起点）。
   *
   * 旧 loop 可能卡在**不可取消**的 `connect()` 内，故用 `restartGraceMs` 有界等待，不无界 `await old`。
   */
  async restart(): Promise<void> {
    this._abort = true; // 请求旧 loop 退出
    this._loopToken += 1; // 立即令旧 loop 失效（不依赖 await）
    const old = this._loopPromise;
    this._running = false;
    this._loopPromise = null;
    if (old) await Promise.race([old.catch(() => undefined), this.sleep(this.restartGraceMs)]);
    this._abort = false;
    void this.start().catch(() => undefined);
  }

  /** 外部判定链路断开（如心跳超时）→ 重启循环（委托 `restart()`，根除并发 loop）。 */
  async reconnect(): Promise<void> {
    return this.restart();
  }

  stop(): void {
    this._abort = true;
    this._running = false;
    this._state = 'disconnected';
    this._activeAddr = null;
  }

  /** 断言已就绪，否则抛 `E_RELAY_UNAVAILABLE`。 */
  assertReady(): void {
    if (this._state !== 'ready' && this._state !== 'connected') {
      throw new MeshLinkError('E_RELAY_UNAVAILABLE', '中继未就绪');
    }
  }
}

export default RelayConnectionManager;
